// Reads the LIVE document as compressed (Open XML) bytes via
// Office.context.document.getFileAsync(Office.FileType.Compressed). This works
// however the document was opened -- it reads what is in the host, not what is on
// disk -- which is exactly what Save needs after the user edited a downloaded draft.
//
// Nothing here is host-specific: Word, Excel and PowerPoint all go through the same
// Common API and differ only in the MIME type they should be labelled with. That is
// why this module moved out of the Word feature folder and takes the type as an
// argument.
//
// Non-obvious constraints this module owns so callers don't have to:
//  - the file arrives in SLICES that must be read in sequence and reassembled;
//  - closeAsync MUST be called on the handle (success or failure) or every
//    subsequent getFileAsync call fails;
//  - only one handle can be open at a time, so concurrent reads are serialised
//    through a module-level chain -- overlapping saves queue instead of failing;
//  - large documents are slow here, so progress is reported per slice for the UI
//    to show real progress rather than a spinner that looks hung.

const SLICE_SIZE = 4 * 1024 * 1024; // 4 MB slices -- fewer round trips for big files.

let readChain: Promise<unknown> = Promise.resolve();

export function getCompressedFile(
  mimeType: string,
  onProgress?: (fraction: number) => void
): Promise<Blob> {
  // Serialise via the chain: a second Save while one is in flight waits its turn.
  const next = readChain.then(() => readWholeFile(mimeType, onProgress));
  readChain = next.catch(() => undefined); // keep the chain alive after failures
  return next;
}

function readWholeFile(mimeType: string, onProgress?: (fraction: number) => void): Promise<Blob> {
  return new Promise<Blob>((resolve, reject) => {
    Office.context.document.getFileAsync(
      Office.FileType.Compressed,
      { sliceSize: SLICE_SIZE },
      (fileResult) => {
        if (fileResult.status !== Office.AsyncResultStatus.Succeeded) {
          reject(new Error(fileResult.error?.message ?? "getFileAsync failed"));
          return;
        }

        const file = fileResult.value;
        const sliceCount = file.sliceCount;
        const slices: ArrayBuffer[] = new Array<ArrayBuffer>(sliceCount);

        const finish = (error?: Error) => {
          // closeAsync is mandatory -- skipping it wedges every later getFileAsync.
          file.closeAsync(() => {
            if (error) {
              reject(error);
            } else {
              resolve(new Blob(slices, { type: mimeType }));
            }
          });
        };

        // Slices are requested strictly in sequence: the API only guarantees ordered
        // delivery when read that way, and the reassembled zip must be byte-ordered.
        const readSlice = (index: number) => {
          if (index >= sliceCount) {
            finish();
            return;
          }

          file.getSliceAsync(index, (sliceResult) => {
            if (sliceResult.status !== Office.AsyncResultStatus.Succeeded) {
              finish(
                new Error(sliceResult.error?.message ?? `getSliceAsync failed at slice ${index}`)
              );
              return;
            }

            const data = sliceResult.value.data as number[] | ArrayBuffer;
            slices[index] = data instanceof ArrayBuffer ? data : new Uint8Array(data).buffer;
            onProgress?.((index + 1) / sliceCount);
            readSlice(index + 1);
          });
        };

        readSlice(0);
      }
    );
  });
}
